import React, { useState, useEffect, useCallback } from 'react';
import { BsCheckCircle } from 'react-icons/bs';
import tournamentService, { Tournament } from '../../../services/tournamentService';
import { useToast } from '../../../hooks/useToast';
import { LoadingSpinner } from '../../../components/ui/LoadingSpinner';

interface TournamentRoundsManagerProps {
  tournament: Tournament;
}

interface Player {
  id: number;
  username: string;
  name: string;
}

interface MatchPairing {
  player1_id: number | null;
  player2_id: number | null;
}

interface Round {
  id?: number;
  round_number: number;
  matches: Array<{
    id?: number;
    player1_id: number;
    player2_id: number;
    player1_guest?: number;
    player2_guest?: number;
    player1?: Player;
    player2?: Player;
    result?: string;
    player1_games?: number;
    player2_games?: number;
  }>;
}

export const TournamentRoundsManager: React.FC<TournamentRoundsManagerProps> = ({
  tournament
}) => {
  const { success, error: showError } = useToast();
  const [players, setPlayers] = useState<Player[]>([]);
  const [rounds, setRounds] = useState<Round[]>([]);
  const [loading, setLoading] = useState(true);
  const [creatingRound, setCreatingRound] = useState(false);
  
  // Estados para la creación manual de rondas
  const [showCreateRound, setShowCreateRound] = useState(false);
  const [newRoundMatches, setNewRoundMatches] = useState<MatchPairing[]>([]);
  
  // Estados para reportar resultados
  const [showReportResult, setShowReportResult] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState<any>(null);
  const [matchResult, setMatchResult] = useState({
    winner: '', // 'player1', 'player2', 'draw'
    player1_games: 0,
    player2_games: 0
  });
  const [reportingResult, setReportingResult] = useState(false);
  
  // Flag para prevenir múltiples cargas simultáneas
  const [hasLoaded, setHasLoaded] = useState(false);

  const loadTournamentData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Cargar jugadores reales del torneo
      const tournamentPlayers = await tournamentService.getTournamentPlayers(tournament.id);
      setPlayers(tournamentPlayers);

      // Cargar matches existentes del torneo
      try {
        const matches = await tournamentService.getTournamentMatches(tournament.id);
        
        // Agrupar matches por ronda
        const roundsMap = new Map<number, Round>();
        
        matches.forEach(match => {
          if (!roundsMap.has(match.round)) {
            roundsMap.set(match.round, {
              round_number: match.round,
              matches: []
            });
          }
          
          const round = roundsMap.get(match.round)!;
          round.matches.push({
            id: match.id,
            player1_id: match.player1?.id || 0,
            player2_id: match.player2?.id || 0,
            player1: match.player1 ? {
              id: match.player1.id,
              username: match.player1.username,
              name: `${match.player1.first_name} ${match.player1.last_name}`.trim()
            } : undefined,
            player2: match.player2 ? {
              id: match.player2.id,
              username: match.player2.username,
              name: `${match.player2.first_name} ${match.player2.last_name}`.trim()
            } : undefined, // Si player2 es null, es un BYE
            result: match.status === 'completed' ? match.result : undefined,
            player1_games: match.player1_games,
            player2_games: match.player2_games
          });
        });
        
        // Convertir el Map a array y ordenar por número de ronda
        const roundsArray = Array.from(roundsMap.values())
          .sort((a, b) => a.round_number - b.round_number);
        
        setRounds(roundsArray);
      } catch (matchesError) {
        console.error('Error loading tournament matches:', matchesError);
        // No es crítico si no se pueden cargar los matches, continuar sin ellos
        setRounds([]);
      }
      
      setHasLoaded(true);
    } catch (err: any) {
      const errorMessage = err instanceof Error ? err.message : 'Error al cargar datos del torneo';
      console.error('Error loading tournament data:', errorMessage);
      
      // En caso de error, mantener algunos jugadores simulados para desarrollo
      if (process.env.NODE_ENV === 'development') {
        const mockPlayers: Player[] = [
          { id: 1, username: 'player1', name: 'Juan Pérez' },
          { id: 2, username: 'player2', name: 'María García' },
          { id: 3, username: 'player3', name: 'Carlos López' },
          { id: 4, username: 'player4', name: 'Ana Martín' },
          { id: 5, username: 'player5', name: 'Pedro González' },
          { id: 6, username: 'player6', name: 'Laura Fernández' },
        ];
        setPlayers(mockPlayers);
      }
      setHasLoaded(true);
    } finally {
      setLoading(false);
    }
  }, [tournament.id]); // Dependencias del useCallback

  useEffect(() => {
    // Solo cargar una vez por componente
    if (!hasLoaded) {
      loadTournamentData();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Solo al montar el componente

  useEffect(() => {
    loadTournamentData();
  }, [tournament.id, loadTournamentData]);

  const initializeNewRound = () => {
    const totalPlayers = players.length;
    const hasOddPlayers = totalPlayers % 2 !== 0;
    const matchCount = Math.ceil(totalPlayers / 2);
    
    const initialMatches: MatchPairing[] = Array(matchCount).fill(null).map((_, index) => {
      // Si es el último match y hay número impar de jugadores, pre-asignar BYE
      if (hasOddPlayers && index === matchCount - 1) {
        return {
          player1_id: null,
          player2_id: -1 // -1 representa BYE
        };
      }
      
      return {
        player1_id: null,
        player2_id: null
      };
    });
    
    setNewRoundMatches(initialMatches);
    setShowCreateRound(true);
  };

  const getAvailablePlayers = (currentMatchIndex: number, isPlayer1: boolean): Player[] => {
    const selectedPlayerIds = new Set<number>();
    
    // Agregar todos los jugadores ya seleccionados en otras partidas
    newRoundMatches.forEach((match, index) => {
      if (index !== currentMatchIndex) {
        if (match.player1_id && match.player1_id !== -1) selectedPlayerIds.add(match.player1_id);
        if (match.player2_id && match.player2_id !== -1) selectedPlayerIds.add(match.player2_id);
      }
    });

    // Si estamos seleccionando player2, también excluir el player1 de esta partida
    if (!isPlayer1 && newRoundMatches[currentMatchIndex].player1_id && newRoundMatches[currentMatchIndex].player1_id !== -1) {
      selectedPlayerIds.add(newRoundMatches[currentMatchIndex].player1_id!);
    }

    // Crear lista de jugadores disponibles + opción BYE
    const availablePlayers = players.filter(player => !selectedPlayerIds.has(player.id));
    
    // Agregar opción BYE si hay número impar de jugadores
    if (players.length % 2 !== 0) {
      availablePlayers.push({
        id: -1,
        username: 'bye',
        name: 'BYE (Descanso)'
      });
    }
    
    return availablePlayers;
  };

  const updateMatchPlayer = (matchIndex: number, playerId: number | null, isPlayer1: boolean) => {
    const updatedMatches = [...newRoundMatches];
    if (isPlayer1) {
      updatedMatches[matchIndex].player1_id = playerId;
    } else {
      updatedMatches[matchIndex].player2_id = playerId;
    }
    setNewRoundMatches(updatedMatches);
  };

  const canSubmitRound = (): boolean => {
    return newRoundMatches.every(match => {
      // Si es un match con BYE, solo necesitamos que player1 esté seleccionado
      if (match.player2_id === -1) {
        return match.player1_id !== null && match.player1_id !== -1;
      }
      // Para matches normales, ambos jugadores deben estar seleccionados
      return match.player1_id !== null && match.player2_id !== null;
    });
  };

    const submitNewRound = async () => {
    if (!canSubmitRound()) {
      showError('Error', 'Debes seleccionar todos los jugadores antes de enviar la ronda');
      return;
    }

    try {
      setCreatingRound(true);

      // Separar matches normales de matches con BYE
      const normalMatches = newRoundMatches
        .map(match => ({
          player1_id: match.player1_id!,
          player2_id: match.player2_id!
        }));

      const byeMatches = newRoundMatches.filter(match => match.player2_id === -1);

      // Llamar al servicio para crear la ronda (solo con matches normales)
      if (normalMatches.length > 0) {
        await tournamentService.createTournamentRound(tournament.id, { matches: normalMatches });
      }

      // TODO: Aquí se podría agregar lógica adicional para registrar los BYEs si el backend lo soporta

      // Recargar los datos del torneo para obtener las rondas actualizadas
      await loadTournamentData();
      
      setShowCreateRound(false);
      setNewRoundMatches([]);
      
      // Calcular el número de ronda que se acaba de crear
      const nextRoundNumber = Math.max(...rounds.map(r => r.round_number), 0) + 1;
      const byeCount = byeMatches.length;
      const successMessage = byeCount > 0 
        ? `Se ha creado la ronda ${nextRoundNumber} con ${normalMatches.length} partidas (${byeCount} jugador${byeCount > 1 ? 'es' : ''} con BYE)`
        : `Se ha creado la ronda ${nextRoundNumber} con ${normalMatches.length} partidas`;
      
      success('Ronda creada', successMessage);
    } catch (err: any) {
      const errorMessage = err instanceof Error ? err.message : 'Error al crear la ronda';
      showError('Error al crear ronda', errorMessage);
    } finally {
      setCreatingRound(false);
    }
  };

  const cancelNewRound = () => {
    setShowCreateRound(false);
    setNewRoundMatches([]);
  };

  // Función para abrir el modal de reportar resultado
  const openReportResult = (match: any) => {
    setSelectedMatch(match);
    setMatchResult({
      winner: '',
      player1_games: match.player1_games || 0,
      player2_games: match.player2_games || 0
    });
    setShowReportResult(true);
  };

  // Función para cancelar el reporte de resultado
  const cancelReportResult = () => {
    setShowReportResult(false);
    setSelectedMatch(null);
    setMatchResult({
      winner: '',
      player1_games: 0,
      player2_games: 0
    });
  };

  // Función para enviar el resultado del match
  const submitMatchResult = async () => {
    if (!selectedMatch || !matchResult.winner) {
      showError('Error', 'Debe seleccionar un ganador');
      return;
    }

    if (matchResult.winner !== 'draw' && matchResult.player1_games === matchResult.player2_games) {
      showError('Error', 'Los juegos deben reflejar al ganador seleccionado');
      return;
    }

    setReportingResult(true);
    try {
      // Para el servicio existente, necesitamos adaptar los datos
      // Siempre reportamos desde la perspectiva del jugador 1
      const resultData = {
        player1_score: matchResult.player1_games,
        player2_score: matchResult.player2_games
      };

      // Llamar al servicio para reportar resultado
      await tournamentService.reportMatchResult(tournament.id, selectedMatch.id, resultData);

      // Recargar los datos del torneo para mostrar el resultado actualizado
      await loadTournamentData();
      
      // Cerrar el modal
      cancelReportResult();
      
      success('Resultado reportado', 'El resultado del match ha sido registrado exitosamente');
    } catch (err: any) {
      const errorMessage = err instanceof Error ? err.message : 'Error al reportar el resultado';
      showError('Error al reportar resultado', errorMessage);
    } finally {
      setReportingResult(false);
    }
  };

  if (loading) {
    return <LoadingSpinner message="Cargando datos del torneo..." />;
  }

  return (
    <div className="space-y-6">
      {/* Header de la sección de rondas */}
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-medium text-gray-900">
            Gestión de Rondas
          </h3>
          <p className="text-gray-600">
            {players.length} jugadores registrados • {rounds.length} {rounds.length === 1 ? 'ronda creada' : 'rondas creadas'}
          </p>
        </div>
        
        {!showCreateRound && (
          <button
            onClick={initializeNewRound}
            disabled={players.length < 2}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            ⚔️ Nueva Ronda
          </button>
        )}
      </div>

      {/* Lista de jugadores */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="text-sm font-medium text-gray-900 mb-3">Jugadores Registrados</h4>
        {players.length === 0 ? (
          <p className="text-sm text-gray-500">No hay jugadores registrados en este torneo.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {players.map((player) => (
              <div key={player.id} className="bg-white rounded px-3 py-2 text-sm">
                {player.name}
                <span className="text-gray-500 ml-2">@{player.username}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Formulario para crear nueva ronda */}
      {showCreateRound && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h4 className="text-lg font-medium text-gray-900">
                Crear Nueva Ronda ({Math.ceil(players.length / 2)} partidas)
              </h4>
              {players.length % 2 !== 0 && (
                <p className="text-sm text-orange-600 mt-1">
                  ⚠️ Número impar de jugadores: un jugador tendrá BYE (descanso automático)
                </p>
              )}
            </div>
            <button
              onClick={cancelNewRound}
              className="text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          </div>

          <div className="space-y-4">
            {newRoundMatches.map((match, index) => (
              <div key={index} className={`rounded-lg p-4 ${
                match.player2_id === -1 ? 'bg-orange-50 border border-orange-200' : 'bg-gray-50'
              }`}>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">
                    Mesa {index + 1}
                    {match.player2_id === -1 && (
                      <span className="ml-2 text-xs text-orange-600 bg-orange-100 px-2 py-1 rounded-full">
                        BYE
                      </span>
                    )}
                  </span>
                </div>
                
                <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Player 1 Selector */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Jugador 1
                    </label>
                    <select
                      value={match.player1_id || ''}
                      onChange={(e) => updateMatchPlayer(index, e.target.value ? parseInt(e.target.value) : null, true)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Seleccionar jugador...</option>
                      {getAvailablePlayers(index, true).map((player) => (
                        <option 
                          key={player.id} 
                          value={player.id} 
                          className={player.id === -1 ? 'text-orange-600 font-medium' : ''}
                        >
                          {player.name}
                          {player.id !== -1 && ` (@${player.username})`}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Player 2 Selector */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Jugador 2
                    </label>
                    <select
                      value={match.player2_id || ''}
                      onChange={(e) => updateMatchPlayer(index, e.target.value ? parseInt(e.target.value) : null, false)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      disabled={!match.player1_id || match.player1_id === -1}
                    >
                      <option value="">
                        {match.player1_id === -1 ? 'BYE (Pre-asignado)' : 'Seleccionar jugador...'}
                      </option>
                      {match.player1_id !== -1 && getAvailablePlayers(index, false).map((player) => (
                        <option 
                          key={player.id} 
                          value={player.id} 
                          className={player.id === -1 ? 'text-orange-600 font-medium' : ''}
                        >
                          {player.name}
                          {player.id !== -1 && ` (@${player.username})`}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Botones de acción */}
          <div className="mt-6 flex justify-end space-x-3">
            <button
              onClick={cancelNewRound}
              className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              onClick={submitNewRound}
              disabled={!canSubmitRound() || creatingRound}
              className="px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {creatingRound ? 'Creando...' : 'Enviar Ronda'}
            </button>
          </div>
        </div>
      )}

      {/* Rondas existentes */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-medium text-gray-900">
            Rondas del Torneo {rounds.length > 0 && `(${rounds.length})`}
          </h4>
          {rounds.length > 0 && (
            <span className="text-xs text-gray-500">
              {rounds.reduce((total, round) => total + round.matches.length, 0)} partidas en total
            </span>
          )}
        </div>
        
        {rounds.length === 0 ? (
          <div className="text-center py-8 bg-gray-50 rounded-lg">
            <div className="mx-auto h-8 w-8 text-gray-400 text-2xl">⚔️</div>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No hay rondas creadas</h3>
            <p className="mt-1 text-sm text-gray-500">
              Crea la primera ronda para comenzar el torneo
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {rounds.map((round) => (
              <div key={round.round_number} className="bg-white border border-gray-200 rounded-lg">
                <div className="px-4 py-3 border-b border-gray-200">
                  <h5 className="text-lg font-medium text-gray-900">
                    Ronda {round.round_number}
                  </h5>
                  <p className="text-sm text-gray-600">
                    {round.matches.length} partidas
                  </p>
                </div>
                
                <div className="p-4">
                  <div className="space-y-3">
                    {round.matches.map((match, index) => (
                      <div key={index} className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
                        <div className="flex items-center space-x-4">
                          <div className="text-sm font-medium">
                            Mesa {index + 1}
                          </div>
                          <div className="flex items-center space-x-2">
                            <span className={`text-sm ${
                              match.result === 'player1_win' ? 'text-green-600 font-semibold' : 'text-gray-900'
                            }`}>
                              {match.player1?.name}
                            </span>
                            
                            {/* Si no hay player2, es un BYE */}
                            {!match.player2 ? (
                              <span className="text-sm text-orange-600 bg-orange-100 px-2 py-1 rounded-full">
                                BYE (Descanso)
                              </span>
                            ) : (
                              <>
                                <div className="flex items-center space-x-1">
                                  <span className="text-xs text-gray-500">
                                    {match.player1_games || 0}
                                  </span>
                                  <span className="text-gray-500">-</span>
                                  <span className="text-xs text-gray-500">
                                    {match.player2_games || 0}
                                  </span>
                                </div>
                                <span className={`text-sm ${
                                  match.result === 'player2_win' ? 'text-green-600 font-semibold' : 'text-gray-900'
                                }`}>
                                  {match.player2?.name}
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex items-center space-x-2">
                          {/* Si no hay player2, es un BYE automático */}
                          {!match.player2 ? (
                            <div className="flex items-center space-x-2">
                              <BsCheckCircle className="h-5 w-5 text-green-500" />
                              <span className="text-xs text-green-600 font-medium">
                                Victoria por BYE
                              </span>
                            </div>
                          ) : match.result ? (
                            <div className="flex items-center space-x-2">
                              <BsCheckCircle className="h-5 w-5 text-green-500" />
                              <div className="text-xs">
                                {match.result === 'player1_win' ? (
                                  <span className="text-green-600 font-medium">
                                    Ganó {match.player1?.name?.split(' ')[0]}
                                  </span>
                                ) : match.result === 'player2_win' ? (
                                  <span className="text-green-600 font-medium">
                                    Ganó {match.player2?.name?.split(' ')[0]}
                                  </span>
                                ) : match.result === 'draw' ? (
                                  <span className="text-yellow-600 font-medium">Empate</span>
                                ) : (
                                  <span className="text-gray-600">Completado</span>
                                )}
                              </div>
                            </div>
                          ) : (
                            <div className="flex items-center space-x-2">
                              {(match.player1_games || 0) > 0 || (match.player2_games || 0) > 0 ? (
                                <span className="text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded">
                                  En progreso
                                </span>
                              ) : (
                                <span className="text-xs text-gray-500 bg-yellow-100 px-2 py-1 rounded">
                                  Pendiente
                                </span>
                              )}
                              {/* Botón para reportar resultado */}
                              <button
                                onClick={() => openReportResult(match)}
                                className="text-xs bg-blue-600 text-white px-2 py-1 rounded hover:bg-blue-700 transition-colors"
                                title="Reportar resultado"
                              >
                                Reportar
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Información adicional */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="text-sm font-medium text-blue-900 mb-2">💡 Información</h4>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• Selecciona manualmente los emparejamientos para cada ronda</li>
          <li>• No se puede repetir un jugador en múltiples partidas de la misma ronda</li>
          <li>• Puedes agregar jugadores invitados (Guest) que no estén registrados en el sistema</li>
          <li>• Se crean {Math.ceil(players.length / 2)} partidas para {players.length} jugadores</li>
          {players.length % 2 !== 0 && (
            <li>• Con número impar de jugadores, uno recibirá BYE (descanso automático y victoria)</li>
          )}
          <li>• Los resultados de las partidas se gestionan desde otra sección</li>
        </ul>
      </div>

      {/* Modal para reportar resultado */}
      {showReportResult && selectedMatch && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Reportar Resultado
                </h3>
                <button
                  onClick={cancelReportResult}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ×
                </button>
              </div>

              <div className="mb-4">
                <div className="text-sm text-gray-600 mb-2">
                  Match: <span className="font-medium">{selectedMatch.player1?.name}</span> vs{' '}
                  <span className="font-medium">{selectedMatch.player2?.name}</span>
                </div>
              </div>

              <div className="space-y-4">
                {/* Selector de ganador */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Resultado *
                  </label>
                  <select
                    value={matchResult.winner}
                    onChange={(e) => setMatchResult(prev => ({ ...prev, winner: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Seleccionar resultado...</option>
                    <option value="player1">Ganó {selectedMatch.player1?.name}</option>
                    <option value="player2">Ganó {selectedMatch.player2?.name}</option>
                    <option value="draw">Empate</option>
                  </select>
                </div>

                {/* Juegos por jugador */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Juegos {selectedMatch.player1?.name}
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="10"
                      value={matchResult.player1_games}
                      onChange={(e) => setMatchResult(prev => ({ 
                        ...prev, 
                        player1_games: parseInt(e.target.value) || 0 
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Juegos {selectedMatch.player2?.name}
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="10"
                      value={matchResult.player2_games}
                      onChange={(e) => setMatchResult(prev => ({ 
                        ...prev, 
                        player2_games: parseInt(e.target.value) || 0 
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={cancelReportResult}
                  className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  onClick={submitMatchResult}
                  disabled={reportingResult || !matchResult.winner}
                  className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {reportingResult ? 'Reportando...' : 'Reportar Resultado'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
